import { useAssetsAPIUploadUpload } from '@mi-front/api-clients';
import { useToast } from '@app/core/notifications';
import { FileInputProps, Spinner } from '@mi-front/ui';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

const HiddenFileInput = styled('input')({
  display: 'none',
});

export interface Props {
  inputProps?: Partial<FileInputProps>;
  onUploaded: (url: string) => void;
}

export function UploadAssetMui(p: Props) {
  const { formatMessage } = useIntl();
  const upload = useAssetsAPIUploadUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { notifyError } = useToast();

  function doUpload(file: File) {
    upload.mutate(
      {
        data: {
          file,
        } as any,
      },
      {
        onSuccess: (data) => {
          p.onUploaded(data.url);
        },
        onError: (error) => {
          notifyError(
            formatMessage({
              id: 'assets.upload.error',
              defaultMessage: 'Ocurrió un error al cargar el archivo, intente nuevamente',
            }),
          );

          console.error(error);
        },
      },
    );
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const maxSizeMb = p.inputProps?.maxSizeMb;

    if (maxSizeMb && files.length && files.find((f: File) => f.size > maxSizeMb * 1024 * 1024)) {
      notifyError(
        formatMessage(
          {
            id: 'assets.upload.errorFileSizeExceeded',
            defaultMessage: 'Los archivos no deben superar {maxSizeMb}MB',
          },
          { maxSizeMb },
        ),
      );
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      return;
    }

    if (files[0]) {
      doUpload(files[0]);
    }

    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <Button
        variant="outlined"
        color="primary"
        size={p.inputProps?.size === 'sm' ? 'small' : 'medium'}
        disabled={upload.isPending}
        startIcon={upload.isPending ? <Spinner /> : <CloudUploadIcon />}
        onClick={handleButtonClick}
      >
        {p.inputProps?.label ||
          formatMessage({
            id: 'assets.upload.label',
            defaultMessage: 'Subir recurso',
          })}
      </Button>
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        multiple={false}
        accept={p.inputProps?.accept}
        onChange={handleFileChange}
        disabled={upload.isPending}
      />
      {p.inputProps?.maxSizeMb && (
        <Box fontSize={'small'}>
          {formatMessage(
            {
              id: 'assets.upload.maxSize',
              defaultMessage: 'Máx. {max}MB.',
            },
            { max: p.inputProps.maxSizeMb },
          )}
        </Box>
      )}
      {p.inputProps?.helperText && <Box fontSize={'small'}>{p.inputProps.helperText}</Box>}
    </Box>
  );
}
